// Rink geometry and background composition. Measurements are off
// assets/rink.png (3200x1600) and match CTH Film Room exactly, so element
// coordinates mean the same thing in both apps.

export const RINK_W = 3200;
export const RINK_H = 1600;
export const SEQ_GAP = 60;  // white band between stacked rinks
export const SEQ_MAX = 20; // rinks in one sequence (was 5 upstream; Tony, 2026-09-23)

// Browsers refuse canvases past a size (Chrome: 32767 px a side; Safari on
// iPhone and iPad: about 16.7 million pixels in all) and a refused canvas
// draws nothing, so a long sequence renders a little smaller instead.
const WEBKIT_ONLY = typeof navigator !== 'undefined' && /AppleWebKit/.test(navigator.userAgent) && !/Chrome|Chromium|Edg/.test(navigator.userAgent);
const CANVAS_MAX_SIDE = 30000;
const CANVAS_MAX_AREA = WEBKIT_ONLY ? 16e6 : 200e6;
/** The largest scale (up to `scale`) at which a w x h canvas still works in this browser. */
export function fitScale(w, h, scale = 1) {
  return Math.min(scale, CANVAS_MAX_SIDE / Math.max(w, h, 1), Math.sqrt(CANVAS_MAX_AREA / Math.max(1, w * h)));
}

export const RINK = {
  goalL: 230, goalR: 2970, blueL: 1135, blueR: 2084, center: 1600,
  creaseL: 272, creaseR: 2928, midY: 800,
  dotRows: [403, 1197],
  dotCols: [564, 1260, 1956, 2638],
};

// The placeable rink items, sized in rink pixels (at 3200 wide).
export const ITEMS = {
  net: { file: 'net', w: 80, h: 140, label: 'Net' },
  coach: { file: 'Co', w: 90, h: 90, label: 'Coach' },
  puck: { file: 'puck', w: 39, h: 39, label: 'Puck' },
  pucks: { file: null, w: 64, h: 56, label: 'Pucks' }, // vector cluster
  cone: { file: 'cone', w: 56, h: 56, label: 'Cone' },
  border: { file: 'border', w: 34, h: 190, label: 'Border' },
};

const SHAPE_FILES = ['net', 'Co', 'puck', 'cone', 'border'];

export function loadImg(src) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => rej(new Error('That image could not be read'));
    img.src = src;
  });
}

let rinkImg = null;
const shapeImgs = new Map(); // name -> HTMLImageElement
const shapeUrls = new Map(); // name -> dataUrl (for SVG hrefs and buttons)

async function toDataUrl(img) {
  const c = document.createElement('canvas');
  c.width = img.naturalWidth; c.height = img.naturalHeight;
  c.getContext('2d').drawImage(img, 0, 0);
  return c.toDataURL('image/png');
}

// THE PLUGIN'S COPY: the art is bundled into main.js as data URLs (esbuild's
// dataurl loader), handed in as { rink, shapes: { name: url } }.
export async function loadAssets(urls) {
  if (rinkImg) return;
  rinkImg = await loadImg(urls.rink);
  await Promise.all(SHAPE_FILES.map(async (name) => {
    const img = await loadImg(urls.shapes[name]);
    shapeImgs.set(name, img);
    shapeUrls.set(name, await toDataUrl(img));
  }));
}

export const shapeImg = (name) => shapeImgs.get(name) || null;
export const shapeUrl = (name) => shapeUrls.get(name) || '';
export const getRinkImg = () => rinkImg;

// Compose the standard background for a sequence of n rinks: white ground,
// rink.png repeated with SEQ_GAP bands. Returns a canvas.
export function composeRinkBg(n = 1) {
  const h = n * RINK_H + (n - 1) * SEQ_GAP;
  const s = fitScale(RINK_W, h); // 1 up to about 18 rinks; callers draw it at RINK_W x h
  const c = document.createElement('canvas');
  c.width = Math.round(RINK_W * s);
  c.height = Math.round(h * s);
  const ctx = c.getContext('2d');
  ctx.scale(s, s);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, RINK_W, h);
  for (let k = 0; k < n; k++) ctx.drawImage(rinkImg, 0, k * (RINK_H + SEQ_GAP), RINK_W, RINK_H);
  return c;
}
